import React, { useCallback, useState, useRef } from 'react';
import { Container, Section } from 'components/shared';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import styled from 'styled-components';
import cx from 'classnames';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { isValidTitle } from 'utils/validateTitle';
import { hasPrice, parsePrice } from 'utils/parsePrice';

import { Button } from 'components/components/atoms';
import {
  CommunityCard,
  CommunityCardMobile,
} from 'components/components/molecules';

const StyledSection = styled(Section)`
  padding-bottom: 3.125rem;
`;

const CarouselContainer = styled.div`
  & ul,
  & .BrainhubCarousel,
  & .BrainhubCarousel__trackContainer {
    overflow: visible !important;
  }
`;

const Heading = styled.h2`
  letter-spacing: -0.5px;
  line-height: 2.625rem;
`;
const Copy = styled.div`
  letter-spacing: 0.11px;
`;

const sortByPrice = (cards) => {
  return [...cards].sort((a, b) => {
    let aPrice = '0';
    let bPrice = '0';
    if (a) aPrice = parsePrice(a.startingFrom === '' ? '0' : a.startingFrom);
    else return -1;
    if (b) bPrice = parsePrice(b.startingFrom === '' ? '0' : b.startingFrom);
    else return 1;
    return aPrice === bPrice
      ? a.title.toLowerCase() > b.title.toLowerCase()
        ? 1
        : -1
      : aPrice > bPrice
      ? 1
      : -1;
  });
};
const getCommunityMPCS = (cards, defaultSort) => {
  try {
    let getMPCs = cards && cards.filter((s) => s.id === s.mpcid);
    for (let i = 0; i < cards.length; i++) {
      const ss = cards[i];
      if (ss.mpcid) {
        if (ss.mpcid !== ss.id) {
          const MPCIndex = getMPCs.findIndex((sc) => sc.id === ss.mpcid);
          if (MPCIndex === -1) {
            getMPCs.push(ss);
          } else {
            if (getMPCs[MPCIndex].Communities) {
              const hasCommunity = getMPCs[MPCIndex].Communities.find(
                (sc) => sc.id === ss.id,
              );
              if (!hasCommunity) getMPCs[MPCIndex].Communities.push(ss);
            } else {
              getMPCs[MPCIndex].Communities = [];
              getMPCs[MPCIndex].Communities.push(ss);
            }
          }
        }
      } else {
        getMPCs.push(ss);
      }
    }
    if (getMPCs) {
      if (defaultSort === 'title') {
        return getMPCs.sort((a, b) =>
          a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1,
        );
      }
      const withNoPrice = getMPCs
        .filter((c) => !hasPrice(c.startingFrom))
        .sort((a, b) => {
          const aHomeType = a.bubbletext ? a.bubbletext : '';
          const bHomeType = b.bubbletext ? b.bubbletext : '';
          return aHomeType === bHomeType
            ? a.title.toLowerCase() > b.title.toLowerCase()
              ? 1
              : -1
            : aHomeType > bHomeType
            ? 1
            : -1;
        });
      const withPrice = sortByPrice(
        getMPCs.filter((c) => hasPrice(c.startingFrom)),
      );
      return [...withPrice, ...withNoPrice];
    }
  } catch (e) {
    return null;
  }
};

const CarouselWrapper = ({ children, isMobile }) =>
  isMobile ? (
    children
  ) : (
    <Container className='text-center'>{children}</Container>
  );

const CommunityCards = ({ fields, rendering }) => {
  const cards = getFieldValue(fields, 'cards') || [];
  const title = getFieldValue(fields, 'title');
  const text = getFieldValue(fields, 'text');
  const blueBackground = getFieldValue(fields, 'blueBackground');
  // ItemList schema for the community cards, on market and MPC pages alike.
  const cardsSchema = getFieldValue(fields, 'cardsSchema');
  const communityDefaultSort = fields.communityDefaultSort;
  const defaultSort =
    communityDefaultSort === 'Alphabetical'
      ? 'title'
      : communityDefaultSort === 'Price'
      ? 'price'
      : 'title';
  const [page, setPage] = useState(1);
  const communityCardsHeadingRef = useRef(null);
  // console.log({communityDefaultSort, defaultSort})

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);

  const slides = getCommunityMPCS(cards, defaultSort);
  let counter = 0;
  const { componentName } = rendering;

  const scrollToHeading = () => {
    if (communityCardsHeadingRef && communityCardsHeadingRef.current) {
      setTimeout(() => {
        const topOffset = isMobile ? 0 : 200;
        window.scrollTo({
          top: communityCardsHeadingRef.current.offsetTop - topOffset,
          left: 0,
          behavior: 'smooth',
        });
      }, 500);
    }
  };

  if (slides.length < 1) return null;

  return (
    <StyledSection
      id={componentName}
      className={cx('pt-16', {
        'bg-light-blue': blueBackground,
        'bg-white': !blueBackground,
      })}
      tabIndex='-1'
    >
      {cardsSchema && (
        <Helmet>
          <script defer type='application/ld+json'>
            {JSON.stringify(cardsSchema)}
          </script>
        </Helmet>
      )}
      <Container className='pt-8 pb-6 lg:px-5' ref={communityCardsHeadingRef}>
        {isValidTitle(title) && (
          <Heading
            className='font-bold font-trade-gothic-20 mb-6 md:mb-2 text-4xl text-mattamy-blue'
            data-aos='fade-down'
          >
            {title}
          </Heading>
        )}
        <Copy
          className='text-normal mb-8 md:mb-2 font-graphie text-mattamy-blue'
          data-aos='fade-down'
          data-aos-delay='100'
          data-aos-offset='100'
        >
          {text}
        </Copy>
      </Container>
      <CarouselWrapper>
        <CarouselContainer className='flex flex-col md:grid md:grid-cols-2 md:gap-4 w-full md:-mb-12'>
          {slides.map((c, i) => {
            if (c.id === c.mpcid) {
              const communites =
                c.Communities &&
                c.Communities.length > 0 &&
                c.Communities.sort((a, b) =>
                  a.communityName.toLowerCase() > b.communityName.toLowerCase()
                    ? 1
                    : -1,
                );
              return (
                <li
                  className='bg-light-blue px-2 rounded flex'
                  id={c.id}
                  key={c.id}
                >
                  {isMobile ? (
                    <>
                      <CommunityCardMobile
                        key={counter}
                        {...c}
                        page={page}
                        counter={counter++}
                        numberPerPage={4}
                      />
                      {communites &&
                        communites.length > 0 &&
                        communites.map((cm) => (
                          <CommunityCardMobile
                            key={counter}
                            {...cm}
                            page={page}
                            counter={counter++}
                            numberPerPage={4}
                          />
                        ))}
                    </>
                  ) : (
                    <>
                      <CommunityCard
                        key={counter}
                        {...c}
                        page={page}
                        counter={counter++}
                        numberPerPage={4}
                      />
                      {communites &&
                        communites.length > 0 &&
                        communites.map((cm) => (
                          <CommunityCard
                            key={counter}
                            {...cm}
                            page={page}
                            counter={counter++}
                            numberPerPage={4}
                          />
                        ))}
                    </>
                  )}
                </li>
              );
            } else {
              return (
                <li
                  key={c.title + c.subtitle}
                  className='bg-white px-2 rounded flex w-ful'
                  id={c.id}
                >
                  {isMobile ? (
                    <CommunityCardMobile
                      key={counter}
                      {...c}
                      page={page}
                      counter={counter++}
                      numberPerPage={4}
                    />
                  ) : (
                    <CommunityCard
                      key={counter}
                      {...c}
                      page={page}
                      counter={counter++}
                      numberPerPage={4}
                    />
                  )}
                </li>
              );
            }
          })}
        </CarouselContainer>
        {page < slides.length / 4 && (
          <Button
            className={cx('mt-12', {
              // "focus:bg-mattamy-blue-dark focus:text-white": !showCondoColor,
              // hidden: !(result.cards.length > desktopVisibleCards),
            })}
            // showCondoColor={showCondoColor}
            // condoColorHexaCode={condoColorHexaCode}
            // color={showCondoColor ? "" : "mattamy-blue"}
            color={'mattamy-blue'}
            variant='filled'
            buttonDescription={'SHOW MORE'}
            onClick={() => {
              setPage(slides.length / 4 + 1);
              scrollToHeading();
            }}
          >
            <p className='w-40'>{'SHOW ALL'}</p>
          </Button>
        )}
      </CarouselWrapper>
    </StyledSection>
  );
};

export default CommunityCards;
